import { ReactNode, forwardRef, useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { useMaintenanceMode } from "@/hooks/useMaintenanceMode";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";

interface ProtectedRouteProps {
  children: ReactNode;
}

export const ProtectedRoute = forwardRef<unknown, ProtectedRouteProps>(function ProtectedRoute(
  { children },
  _ref,
) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [checkingApproval, setCheckingApproval] = useState(true);
  const [isApproved, setIsApproved] = useState<boolean | null>(null);
  const checkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasCheckedRef = useRef(false);
  const { isMaintenanceMode, maintenanceMessage, loading: maintenanceLoading } = useMaintenanceMode();
  const { isSuperAdmin, loading: superAdminLoading } = useSuperAdmin();

  useEffect(() => {
    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
    }

    const checkApproval = async () => {
      if (!user) {
        setCheckingApproval(false);
        return;
      }

      if (hasCheckedRef.current) {
        return;
      }
      hasCheckedRef.current = true;

      try {
        checkTimeoutRef.current = setTimeout(() => {
          console.warn("Approval check timed out, defaulting to denied");
          setIsApproved(false);
          setCheckingApproval(false);
        }, 8000);

        const { data, error } = await supabase.rpc("can_user_access_portal", {
          _user_id: user.id,
        });

        if (checkTimeoutRef.current) {
          clearTimeout(checkTimeoutRef.current);
          checkTimeoutRef.current = null;
        }

        if (error) {
          logger.error("Error checking user approval:", error);
          setIsApproved(false);
        } else {
          setIsApproved(data);
        }
      } catch (err) {
        logger.error("Error checking approval:", err);
        setIsApproved(false);
      } finally {
        setCheckingApproval(false);
      }
    };

    if (!loading && user) {
      checkApproval();
    } else if (!loading) {
      setCheckingApproval(false);
    }

    return () => {
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
  }, [user, loading]);

  useEffect(() => {
    hasCheckedRef.current = false;
  }, [user?.id]);

  if (loading || checkingApproval || maintenanceLoading || superAdminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (!isApproved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="mx-auto w-16 h-16 rounded-full bg-warning/20 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-warning"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground">
              Cuenta Pendiente de Aprobación
            </h2>
            <p className="mt-2 text-muted-foreground">
              Tu cuenta ha sido creada correctamente, pero necesita ser activada por un administrador antes de poder acceder al portal.
            </p>
            <p className="mt-4 text-sm text-muted-foreground">
              Contacta al administrador para solicitar la activación de tu cuenta.
            </p>
          </div>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => {
                hasCheckedRef.current = false;
                setCheckingApproval(true);
                setIsApproved(null);
              }}
              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
            >
              Reintentar
            </button>
            <button
              onClick={async () => {
                await supabase.auth.signOut();
                window.location.href = "/";
              }}
              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isMaintenanceMode && !isSuperAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="mx-auto w-16 h-16 rounded-full bg-warning/20 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-warning"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.573-1.066z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground">
              Portal en Mantenimiento
            </h2>
            <p className="mt-2 text-muted-foreground">
              {maintenanceMessage || "Estamos realizando mejoras en el sistema. Por favor, vuelve a intentarlo más tarde."}
            </p>
          </div>
          <button
            onClick={async () => {
              await supabase.auth.signOut();
              window.location.href = "/";
            }}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
          >
            Cerrar Sesión
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
});

ProtectedRoute.displayName = "ProtectedRoute";
